import type { WricefDecisionSummary } from "@/api/wricef-decisions";

/**
 * A sugestão da seção de extensões da Calibragem a partir do Catálogo WRICEF
 * (#1712, cartão 3.1).
 *
 * O Marco decidiu em 22/10/2026 que a Calibragem traz o escopo decidido no
 * Catálogo: a quantidade por estratégia e as horas do arquiteto, com o consultor
 * podendo sobrescrever. O motor multiplica quantidade por horas por objeto, então
 * aceitar a sugestão grava as horas do arquiteto divididas pela quantidade: o
 * produto volta às horas do arquiteto sem conta nova no motor. O arredondamento
 * em duas casas muda o total em menos de um centésimo de hora por objeto.
 *
 * O em escopo com estratégia que a Calibragem não tem (sem estratégia, ou
 * `classic`) fica à parte, para o consultor decidir, e o pendente com o cliente
 * aparece com as horas, fora do total.
 */

/** Estratégia do Catálogo -> os dois parâmetros da Calibragem, na ordem da tela. */
export const STRATEGY_PARAMETERS = [
  { strategy: "key_user", quantityKey: "extKeyUser", hoursKey: "hhExtKeyUser" },
  { strategy: "developer", quantityKey: "extDeveloper", hoursKey: "hhExtDeveloper" },
  { strategy: "side_by_side", quantityKey: "extBtp", hoursKey: "hhExtBtp" },
] as const;

export interface SuggestionRow {
  strategy: string;
  count: number;
  hours: number;
}

export interface ExtensionSuggestion {
  /** As três estratégias que a Calibragem tem, só as com objeto. */
  rows: SuggestionRow[];
  /** Em escopo sem parâmetro na Calibragem: sem estratégia ou `classic`. */
  apart: SuggestionRow[];
  /** Pendentes com o cliente: à parte, fora do total. */
  pending: { count: number; hours: number };
  /** Em escopo sem hora: contados à parte, nunca somados como zero. */
  missingHours: number;
  /** As horas das três estratégias, que é o que a sugestão leva ao total. */
  total: number;
  /** O que aceitar a sugestão grava nos parâmetros. */
  overrides: Record<string, number>;
}

function bucket(value: unknown): { count: number; hours: number } {
  const record = (value ?? {}) as { count?: number; hours?: number };
  return { count: record.count ?? 0, hours: record.hours ?? 0 };
}

export function extensionSuggestion(
  summary: WricefDecisionSummary | undefined,
): ExtensionSuggestion | null {
  if (!summary || summary.decided === 0) return null;
  const byStrategy = summary.in_scope_by_strategy ?? {};
  const mapped = new Set<string>(STRATEGY_PARAMETERS.map((entry) => entry.strategy));

  const rows: SuggestionRow[] = [];
  const overrides: Record<string, number> = {};
  for (const { strategy, quantityKey, hoursKey } of STRATEGY_PARAMETERS) {
    const { count, hours } = bucket(byStrategy[strategy]);
    overrides[quantityKey] = count;
    if (count > 0) {
      rows.push({ strategy, count, hours });
      overrides[hoursKey] = Math.round((hours / count) * 100) / 100;
    }
  }
  const apart = Object.entries(byStrategy)
    .filter(([strategy]) => !mapped.has(strategy))
    .map(([strategy, value]) => ({ strategy, ...bucket(value) }))
    .filter((row) => row.count > 0);

  return {
    rows,
    apart,
    pending: {
      count: summary.by_decision?.client_validation ?? 0,
      hours: summary.hours_by_decision?.client_validation ?? 0,
    },
    missingHours: summary.in_scope_missing_hours ?? 0,
    total: rows.reduce((sum, row) => sum + row.hours, 0),
    overrides,
  };
}

/** A sugestão já está nos parâmetros: cada valor dela é o que vale na tela. */
export function suggestionApplied(
  suggestion: ExtensionSuggestion,
  values: Record<string, number>,
): boolean {
  return Object.entries(suggestion.overrides).every(([key, value]) => values[key] === value);
}
